/**
 * The matte lifecycle: run it, key it onto the source, re-key it, replay it, restore it.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-7 / AUD-2 RC-4) with no change to a single body. AUD-2
 * calls this the highest-risk seam in the wave and it is: `applyMatteToSource` carries the scars of
 * three field bugs (US3 "it removes the case", B3 matte strength, F1's restore-calibration
 * override) and its CHROMA branch is WS-A's byte-identity contract with the Transparent tab and
 * with `scripts/transparent_overlay.py`. Every comment below travelled with the line it explains —
 * AUD-2 says outright that those comments "are the only reason the file is currently correct."
 *
 * What this hook owns:
 *  - `autoRemoveBg` (the on-device AI pass, its model download, its two timeouts and ED-9's
 *    cancel/stale machinery), `cancelAutoRemoveBg`, `removeByMagentaKey` and its automatic twin
 *    `autoMagentaKey` (WS-AUTOKEY);
 *  - `applyMatteToSource` + `matteParamsSig` (the F1 calibration override, the F6 scratch reuse);
 *  - `rekeyChroma`, `printAlphaFor` (WS-I's precedence + its `print mask from …` log),
 *    `reapplyMatte`;
 *  - the marker-cache invalidation on `[markerColor, markerTol]`, the "Keyed %" readout, the B5
 *    matte-only re-key debounce;
 *  - `runMatteRestore` and its failure LATCH, which is what makes Retry strictly user-initiated.
 *
 * The loaded-model cache `autoRemoveBg` reads and writes now lives in
 * `editor/segmentationModel.ts`, where J-14 gave it a refcount + a 30 s grace window so a view
 * switch no longer evicts ~109 MB (F-35).
 */
import { useEffect, useRef, useState } from "react";
import type { EngineBinding, MagentaMarkerResult, Polygon } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import type { EditorRefs } from "./editorContext.js";
import type { AutoKeyVerdict, ComposeReason, MaskEditorProps, MatteKind } from "./maskEditorTypes.js";
import { coverageFailure } from "./coverageCheck.js";
import { chromaKeyFromPristine, chromaMarkerFromPristine, inkFraction, type GuideKeyOptions } from "./chromaKey.js";
import { despillGreen, featherMask, refineMatte, remapMatteStrength } from "./chroma.js";
import { detectTransparentRegion, keyedPrintQuad, printQuadFromAlpha } from "./placement.js";
import { decodeMaskAlpha, dataUrlToBytes, get2dContext } from "./pixels.js";
// F-38: the protect-product fill, once per ROW instead of once per pixel.
import { forEachPixelInPolyNorm } from "./scanline.js";
import { resolveCalibration, nativeMaskParams, guideKeyOptions } from "../stores/segmenterCalibration.js";
import {
  isModelCachedById,
  getSegmentationModelForQuality,
  modelIdForQuality,
  formatSpeed,
  formatEta,
} from "../stores/modelStore.js";
import { fetchBytesWithTimeout, MATTE_RESTORE_TIMEOUT_MS, NO_HTTP_CACHE } from "../templates/timeout.js";
// F-35 / J-14 — the ~109 MB model outlives a remount; see `segmentationModel.ts`.
import { loadedSegmentationModel, setLoadedSegmentationModel } from "./segmentationModel.js";

/** Host of a hosted-asset URL for structured logs — never the full URL (it can carry a token). */
function hostOfUrl(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}

/**
 * WS-R / AUD-8 C-3: protect-product as a RAMP over the polygon boundary instead of a stamp.
 *
 * `protectRampPx = 0` (and any negative value) is the EXACT legacy hard stamp — byte-identical,
 * and that is what `use-matte.test.ts`'s existing cases keep asserting. Above 0, alpha is blended
 * toward the original over a band of that width straddling the polygon edge, using the same
 * smoothstep `t * t * (3 - 2 * t)` `softAiEdgeRefine` uses for the other hard cut in this chain,
 * and the same `rampWidth <= 0 ⇒ exact legacy behaviour` escape hatch.
 *
 * THE DEFECT IT FIXES: the legacy block restores the original alpha for every pixel strictly
 * inside an operator-drawn quad and leaves every pixel outside untouched — a binary discontinuity
 * along a line that need not coincide with any real product edge.
 *
 * WHY A BLUR AND NOT A DISTANCE PASS: WS-J's F-38 replaced a per-pixel `pointInPolyNorm`
 * (4.19 M px × 4 edges ≈ 16.8 M non-inlined operations, 58 ms at 2048²) with one evaluation per
 * ROW, and a 58 ms regression on every slider drag would be worse than the seam. So the ramp is a
 * separable box blur (`featherMask`, O(w·h) REGARDLESS of radius — the editor's own function, not
 * a second implementation) of the scanline's own visited set. The polygon walk is unchanged and
 * is evaluated exactly as many times at `rampPx = 32` as at `rampPx = 0`; `use-matte.test.ts`
 * asserts that directly.
 *
 * MEASURED BY `E` (seam step) in `scripts/cleaner_quality.py`: the four headline metrics are
 * seam-BLIND by construction (the seam lies inside the product, away from the ground truth's
 * boundary band), which is why C-3 needs its own scalar and why that scalar has been reported
 * since R-1 — before the change it exists to judge.
 *
 * Mutates `keyed`'s ALPHA channel in place; never reads or writes `base` except to read alpha.
 */
export function protectProductAlpha(
  keyed: Uint8ClampedArray,
  base: Uint8ClampedArray,
  width: number,
  height: number,
  printArea: Polygon,
  rampPx: number,
): void {
  if (printArea.length < 3) return;
  if (!(rampPx > 0)) {
    // LEGACY, unchanged: one scanline pass, restore the original alpha, nothing else.
    forEachPixelInPolyNorm(width, height, printArea, (i) => {
      const o = i * 4;
      keyed[o + 3] = base[o + 3];
    });
    return;
  }
  const n = width * height;
  // One scanline pass — the SAME walk the hard stamp does — into a binary coverage plane.
  const cov = new Uint8Array(n);
  forEachPixelInPolyNorm(width, height, printArea, (i) => {
    cov[i] = 255;
  });
  // `featherMask` clamps its window to in-bounds neighbours and divides by the live tap count,
  // so the ramp stays centred on the edge even where the quad runs off the frame.
  const soft = featherMask(cov, width, height, Math.round(rampPx));
  for (let i = 0; i < n; i++) {
    const c = soft[i];
    if (c === 0) continue; // untouched: outside the ramp entirely
    const o = i * 4;
    const a = base[o + 3];
    if (c === 255) {
      keyed[o + 3] = a; // fully inside: the legacy restore, exactly
      continue;
    }
    const t = c / 255;
    const s = t * t * (3 - 2 * t);
    const k = keyed[o + 3];
    keyed[o + 3] = Math.round(k + (a - k) * s);
  }
}

export interface UseMatteArgs {
  engine: EngineBinding;
  shadedMagentaGuide?: boolean;
  /** The print guide key's options as the editor's sliders hold them; absent → the platform's. */
  guideKey?: GuideKeyOptions;
  locked?: boolean;
  logger?: AppLogger;
  versionId?: string;
  onBaked?: unknown;
  initialLayerRefs?: MaskEditorProps["initialLayerRefs"];
  /** The nine shared refs (J-3). */
  editorRefs: EditorRefs;

  // --- source (J-6) ---
  chromaReady: boolean;
  readSource: () => { data: Uint8ClampedArray; width: number; height: number } | null;
  snapToEdges: (
    quad: Polygon,
    src: { data: Uint8ClampedArray; width: number; height: number },
  ) => Polygon;
  pristineRgbaRef: React.RefObject<ImageData | null>;
  originalBytesRef: React.RefObject<Uint8Array | null>;
  /** The geometric view being authored (B4). A CHANGE resets the per-view matte state (F-35). */
  view?: string;
  /** Resolved display width/height — `runMatteRestore` decodes the saved matte at this size. */
  width: number;
  height: number;

  // --- geometry ---
  printArea: Polygon;
  onPrintAreaChange: (p: Polygon) => void;

  // --- live editor state the matte reads ---
  aiBusy: boolean;
  setAiBusy: (v: boolean) => void;
  matteQuality: "fast" | "quality";
  markerColor: [number, number, number] | null;
  markerTol: number;
  protectProduct: boolean;
  /**
   * WS-R / AUD-8 C-3: width in px of the protect-product ramp. **Default 0 — the exact legacy
   * hard stamp.** The ramp ships DISABLED and the harness decides whether the default moves:
   * changing a default that every existing template's bake depends on, on a perceptual argument
   * alone, is exactly the move AUD-8 §3.2 says this codebase has been making for years.
   */
  protectRampPx?: number;
  removed: boolean;
  setRemoved: (v: boolean) => void;
  setRemovalVer: (f: (v: number) => number) => void;
  setPlacementMsg: (m: string | null) => void;

  // --- calibration (the six inputs `matteParamsSig` hashes) ---
  edgeChoke: number;
  edgeFeather: number;
  matteStrength: number;
  stageDespill: boolean;
  stageRefine: boolean;
  stageMatteStrength: boolean;
  setEdgeChoke: (v: number) => void;
  setEdgeFeather: (v: number) => void;
  setMatteStrength: (v: number) => void;
  aiEdge: boolean;
  setAiEdge: (v: boolean) => void;
  aiEdgeKeep: number;
  setAiEdgeKeep: (v: number) => void;

  // --- late-bound bridges to hooks this one cannot import ---
  /** Set by `useLayerAssets`; the B5 debounce's gate reads it. */
  testDesignRef: React.RefObject<Uint8Array | null>;
  /** Set by `useMockupBake`; `autoRemoveBg` triggers the persisting upload-bake through it. */
  previewMockupRef: React.RefObject<(uploadBake?: boolean, reason?: ComposeReason) => Promise<boolean>>;
  /** From `useEditorHistoryBridge` — ED-1 / ED-8 / ED-9. */
  restoreRemovalRef: React.RefObject<boolean>;
  undoneMatteKindRef: React.RefObject<MatteKind | null>;
  applyMatteToSourceRef: React.RefObject<((alpha: Uint8Array | Uint8ClampedArray) => boolean) | null>;
  /** The job's product height the coverage band scales by; `null` = no height restriction. */
  coverageHeightPct: number | null;
}

/** The automatic key's answer: the verdict and, when it failed, the named reason. */
export interface AutoKeyAnswer {
  verdict: AutoKeyVerdict | "skipped";
  reason?: string;
}

export function useMatte(args: UseMatteArgs) {
  const shadedMagentaGuide = args.shadedMagentaGuide ?? false;
  const {
    engine,
    locked,
    logger,
    versionId,
    onBaked,
    initialLayerRefs,
    editorRefs,
    chromaReady,
    readSource,
    snapToEdges,
    pristineRgbaRef,
    originalBytesRef,
    view,
    width: w,
    height: h,
    printArea,
    onPrintAreaChange,
    aiBusy,
    setAiBusy,
    matteQuality,
    markerColor,
    markerTol,
    protectProduct,
    protectRampPx = 0,
    removed,
    setRemoved,
    setRemovalVer,
    setPlacementMsg,
    edgeChoke,
    edgeFeather,
    matteStrength,
    stageDespill,
    stageRefine,
    stageMatteStrength,
    setEdgeChoke,
    setEdgeFeather,
    setMatteStrength,
    aiEdge,
    setAiEdge,
    aiEdgeKeep,
    setAiEdgeKeep,
    coverageHeightPct,
    testDesignRef,
    previewMockupRef,
    restoreRemovalRef,
    undoneMatteKindRef,
    applyMatteToSourceRef,
  } = args;
  const guideOptions = args.guideKey ?? guideKeyOptions();

  /** Cached magenta-marker detection (print mask + quad + despilled) from the last
   *  Remove background, reused by Preview mockup so it keys once. */
  const markerRef = useRef<MagentaMarkerResult | null>(null);
  /** Which technique produced the matte currently on the canvas. `"chroma"` (the Magenta key)
   *  writes `keyImage`'s own despilled RGBA verbatim and bypasses every AI-matte stage, so every
   *  re-key path must RE-RUN the key instead of replaying the alpha plane through the pipeline —
   *  replaying it discards the despilled colours and lets protect/choke/feather eat the panel.
   *  Null until a removal has run. */
  const matteKindRef = useRef<MatteKind | null>(null);
  /** True once the user DELIBERATELY reverted the AI removal this session ("Restore background").
   *  Distinguishes a matte-less bake caused by an intentional clear (→ drop the saved matte) from
   *  one that fired before the async matte-restore finished (→ carry the saved matte forward). Set in
   *  `resetBackground`; cleared whenever a matte is (re)applied or the case image changes. */
  const matteClearedRef = useRef(false);
  /** F6 perf: component-scoped scratch reused by `applyMatteToSource` so a slider drag
   *  (a re-key every ~320ms) doesn't churn fresh full-frame buffers through the GC.
   *  `scratchRgbaRef` holds the keyed RGBA copy of `base` (reallocated only when the
   *  frame size changes); `scratchMatteRef` is the strength-remapped matte plane. Both
   *  are OWNED by applyMatteToSource, never alias `pristineRgbaRef`/`matteAlphaRef`/the
   *  matte, and are copied out (into the canvas ImageData) before the next re-key. */
  const scratchRgbaRef = useRef<Uint8ClampedArray | null>(null);
  const scratchMatteRef = useRef<Uint8Array | null>(null);
  /** Monotonic id for each auto-clean run. A run captured this id at start; if a newer run
   *  (or a source reset) bumps it, the older run must NOT commit its matte to the canvas —
   *  prevents a slow/timed-out removal from painting a stale mask onto a swapped image. */
  /**
   * The print quad DETECTION last wrote, or null when detection has not written one this session.
   *
   * Provenance is derived from this VALUE rather than latched by a flag: the print area belongs to
   * detection while it is still byte-for-byte what detection produced, and to the OPERATOR the
   * moment it differs. Seed rectangle, a corner drag, an arrow-key nudge and a vertex insert or
   * delete therefore all claim it without any of those paths having to know this rule exists — and
   * an Undo back onto the detected quad hands it back, which a latch could not do.
   *
   * Null until detection runs, so a print area a template arrives with reads as the operator's and
   * a key KEEPS it. That is deliberate: its real provenance is not stored anywhere, and of the two
   * possible mistakes, re-detecting over corners somebody placed is the one that destroys work.
   */
  const detectedPrintAreaRef = useRef<Polygon | null>(null);
  /** WS-AUTOKEY fix round — the LAST key asked for a print area and none could be fitted to
   *  what it keyed. `keyMagenta` reads it: a key with no print area is not "keyed". */
  const printAreaMissedRef = useRef(false);
  /** The last chroma key's keyed fraction — the owner's coverage check reads it. */
  const keyCoverageRef = useRef(0);
  /** True when the current print area is the operator's to keep — see `detectedPrintAreaRef`. */
  const printAreaIsByHand = () => {
    if (printArea.length === 0) return false;
    const d = detectedPrintAreaRef.current;
    if (!d || d.length !== printArea.length) return true;
    return printArea.some((p, i) => p.x !== d[i].x || p.y !== d[i].y);
  };
  /** Write a DETECTED print area and remember it as detection's, so a later key may replace it. */
  const writeDetectedPrintArea = (q: Polygon) => {
    detectedPrintAreaRef.current = q;
    onPrintAreaChange(q);
  };
  /** What the status line says when a key or a removal left the operator's corners alone. */
  /**
   * What the status line says when a key or a removal left an existing print area alone.
   *
   * It has to fit BOTH cases this rule covers: an area the operator drew in this session, and one
   * the template simply arrived with (provenance is session-scoped, so a stored area reads as
   * theirs). "Move a corner back" fitted only the first — there is nothing to move back to on a
   * template that came with its area already set.
   *
   * It also names only controls that are on screen in this exact state. "Seed rectangle" is not:
   * `controls/CutoutToolsCard.tsx` renders it only while `printArea.length < 3`, which is never
   * true when this message appears. Clearing the area is not a control at all. Dragging a corner
   * always works on an unlocked template, and Undo is the one path back to a detected area.
   */
  const KEPT_BY_HAND =
    "Kept the print area that was already set — the key replaces only an area it detected itself. " +
    "Drag a corner to adjust it, or undo your corner edits to let the key detect it again.";

  const autoRemoveReqRef = useRef(0);
  /** ED-9 — true while an `autoRemoveBg` pass is STILL EXECUTING, cancelled or not. `aiBusy` is a
   *  UI flag that Cancel clears immediately; this one only clears when the (multi-GB) native
   *  inference actually returns, so a post-Cancel click can't start a second concurrent pass. */
  const aiRunInFlightRef = useRef(false);
  /** Always points at the latest `reapplyMatte` closure so the (stable) imperative handle invokes
   *  the current one (it captures live calibration/protect state; a stale closure would replay old
   *  params). Mirrors previewMockupRef. */
  const reapplyMatteRef = useRef<() => boolean>(() => false);
  /**
   * MN-3 / J-13 — the STABLE identity of `initialLayerRefs`.
   *
   * `TemplateEditor` rebuilds that object every render, so any effect keyed on its identity fires
   * on every unrelated re-render. Keying on its VALUE makes "the saved refs changed" mean what it
   * says. Computed here, in the editor, without touching `TemplateEditor.tsx` (WS-K2 owns that).
   */
  const layerRefsKey = JSON.stringify(initialLayerRefs ?? null);
  /** Live "transmission"/coverage readout: the % of the image the picked colour + tolerance
   *  keys to transparency, so the operator SEES the effect of playing with the shade/tolerance
   *  (pure colour → keyed 100%, drifting shades → partially, per the soft ramp). null = no pick. */
  const [keyedPct, setKeyedPct] = useState<number | null>(null);

  // The marker detection is cached (keys once), but the picked marker COLOUR + TOLERANCE feed
  // the keyer — so invalidate the cache when they change, else Preview mockup keeps reusing the
  // stale detection and the tolerance/colour slider appears to do nothing.
  useEffect(() => {
    markerRef.current = null;
  }, [markerColor, markerTol, shadedMagentaGuide]);

  // Live transmission/coverage readout: when a marker colour is picked, re-key on a short
  // debounce as the operator plays with the colour/tolerance, so they SEE how much of the image
  // the current pick turns transparent (the % they asked for) instead of a silent slider. The
  // classical CV keyer is cheap; this also pre-populates the marker cache so Preview reuses it.
  useEffect(() => {
    if (locked || !chromaReady) {
      setKeyedPct(null);
      return;
    }
    // WS-I: with NO colour picked the AUTO rule is the chroma key, and it is the same key the
    // canvas already ran — so the readout is computed synchronously here rather than left null.
    // "Keyed %" is now live on the DEFAULT path, not only after an eyedropper pick.
    if (!markerColor) {
      const pristine = pristineRgbaRef.current;
      if (!pristine) { setKeyedPct(null); return; }
      const chroma = chromaMarkerFromPristine(pristine.data, pristine.width, pristine.height, shadedMagentaGuide, guideOptions);
      setKeyedPct(chroma ? Math.round(chroma.coverage * 100) : null);
      return;
    }
    if (!engine.detectMagentaMarker) {
      setKeyedPct(null);
      return;
    }
    const orig = originalBytesRef.current;
    if (!orig) return;
    let cancelled = false;
    const id = setTimeout(() => {
      void engine
        .detectMagentaMarker!(orig, 0, markerColor, markerTol)
        .then((m) => {
          if (cancelled) return;
          markerRef.current = m;
          setKeyedPct(Math.round(m.coverage * 100));
        })
        .catch(() => {
          if (!cancelled) setKeyedPct(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
    // MN-11 / review-1 M-3: `pristineRgbaRef` / `originalBytesRef` arrive as ARGS, so the rule
    // cannot see they are refs and treats them as ordinary values. Listing them satisfies it and
    // re-fires nothing — a ref object's identity is stable for the life of the owning component.
  }, [markerColor, markerTol, chromaReady, locked, engine, pristineRgbaRef, originalBytesRef, shadedMagentaGuide]);

  /**
   * ED-9 — cancel an in-flight AI removal.
   *
   * The LOCAL neutralization (stale the run id, release the UI) is the part that has always
   * held: a late native result is discarded at the commit site, `aiRunInFlightRef` still blocks
   * a concurrent second inference until the pass actually returns, and every UI write in the run
   * goes through `stale()`. What it never did was STOP the inference, which kept burning CPU/RAM
   * to completion.
   *
   * So ask the engine to abort too, when it can. `cancelBackgroundRemoval` is OPTIONAL and
   * feature-detected: the web/wasm binding and the test doubles don't have it, and there the
   * behaviour is exactly what it was before. The cast keeps this compiling against a binding
   * that hasn't declared the method yet (it is landing separately); it must never be the thing
   * that breaks the cancel, so a throwing/rejecting binding is swallowed — the local
   * neutralization below runs either way.
   */
  const cancelAutoRemoveBg = () => {
    const cancelNative = (engine as EngineBinding & { cancelBackgroundRemoval?: () => unknown })
      .cancelBackgroundRemoval;
    if (cancelNative) {
      try {
        const r = cancelNative.call(engine) as Promise<unknown> | undefined;
        if (r && typeof r.then === "function") void r.catch(() => {});
      } catch { /* a binding-level abort failure must not block the local cancel */ }
    }
    autoRemoveReqRef.current += 1; // stale the in-flight run (a late result is discarded)
    setAiBusy(false);
    setPlacementMsg("Background removal cancelled.");
    logger?.log(
      "info",
      `Editor · AI removal cancelled by the user${cancelNative ? " (native inference aborted)" : ""}`,
    );
  };

  /**
   * Remove the key color: bake matching pixels to alpha 0 on the SOURCE canvas so
   * the displayed image goes transparent there, then immediately auto-detect the
   * placement quad from that transparent region. Pixel math is in
   * chroma.applyChromaKey; here we paint it back to the canvas + re-detect.
   */
  /** One-click AUTOMATIC background removal. Prefers the on-device U²-Net segmenter
   *  (desktop `ml` build) for a clean neural mask; falls back to the classical
   *  border-flood remover (web / no model). Non-destructive: the original is always
   *  restorable. Then auto-detects the edge-snapped placement quad. */
  /** No-AI removal: the SAME chroma key the Transparent tab ships (transparent/keying.ts,
   *  twin of scripts/transparent_overlay.py). The magenta panel becomes the cutout —
   *  deterministic, on-device, instant, no model download. Unlike the AI matte it goes onto
   *  the canvas VERBATIM (see `rekeyChroma` / the chroma branch of applyMatteToSource): the
   *  key already despilled the panel, so the AI-matte stages would only corrupt it. */
  /**
   * The one body behind BOTH ways the magenta key runs: the "Print guide key (no AI)" button and
   * WS-AUTOKEY's automatic key on a Higgsfield stage-2 hand-over. Same keyer, same print-area
   * rule, same pixels — only the words differ, and `quiet` drops the refusal sentence for a
   * host that says it in its own words (the customer's Mask step has no eyedropper to name).
   *
   * `"skipped"` is NOT a verdict: the canvas could not be read (or a removal is running), so
   * nothing is known about the image yet and the caller may try again.
   */
  const keyMagenta = (opts: { auto: boolean; quiet: boolean }): AutoKeyAnswer => {
    if (locked || aiBusy) return { verdict: "skipped" };
    if (!readSource()) return { verdict: "skipped" }; // unreadable canvas — not a "no magenta" verdict
    if (!rekeyChroma({
      detectPrintArea: true,
      message: opts.auto
        ? "Print area keyed automatically — no AI."
        : "Print guide keyed — the Transparent tab's exact technique, no AI.",
    })) {
      // WS-I / D-11: the AUTO path is magenta-only in BOTH keyers now
      // (`detect::marker::GREEN_MARKER_ENABLED = false`), so a green/teal/other-coloured panel
      // lands here. Dropping automatic green is only defensible because the escape hatch still
      // works and this message names it — the eyedropper's picked-colour path keys any colour.
      if (!opts.quiet) {
        setPlacementMsg("No print guide found — the chroma key needs the print guide. If this panel is another colour (green, teal, a drifted guide colour), pick it with the marker-colour eyedropper below. For a photo with no marker at all, use Remove background (AI).");
      }
      logger?.log("warn", `Editor · Print guide key (no AI) found no magenta panel${opts.auto ? " (automatic key)" : ""}`);
      return { verdict: "not-found", reason: "autokey_not_found: no magenta panel in the image" };
    }
    if (printAreaMissedRef.current) {
      // The panel keyed, but no print area could be fitted to it: there is nothing to place a
      // design in, so this is not "keyed" — a customer's Publish stays locked and the host
      // shows its plain failure sentence.
      if (!opts.quiet) {
        setPlacementMsg("Print guide keyed, but no print area could be fitted to it. Draw the four corners with Seed rectangle in Print area & cutouts.");
      }
      logger?.log("warn", `Editor · Print guide key (no AI) keyed but fitted no print area${opts.auto ? " (automatic key)" : ""}`);
      return { verdict: "not-found", reason: "autokey_not_found: no print area could be fitted" };
    }
    const offCoverage = coverageFailure(keyCoverageRef.current, coverageHeightPct);
    if (opts.auto && offCoverage) {
      if (!opts.quiet) setPlacementMsg(`Print guide keyed, but ${offCoverage.split(": ")[1]}.`);
      logger?.log("warn", `Editor · automatic magenta key applied with a coverage advisory — ${offCoverage}`);
      return { verdict: "off-coverage", reason: offCoverage };
    }
    logger?.log("info", `Editor · Print guide key (no AI) applied${opts.auto ? " automatically" : ""}`);
    return { verdict: "keyed" };
  };
  const removeByMagentaKey = () => { keyMagenta({ auto: false, quiet: false }); };
  /** WS-AUTOKEY — the automatic key. See `keyMagenta`. */
  const autoMagentaKey = (quiet: boolean) => keyMagenta({ auto: true, quiet });

  const autoRemoveBg = async () => {
    if (locked || aiBusy) return; // re-entrancy guard (the first run may download ~109 MB)
    // ED-9: "Cancel" only STALES the run (it releases the UI); the native inference keeps going
    // and can hold multiple GB. Without this second guard a click right after Cancel started a
    // CONCURRENT multi-GB pass on top of the one still finishing.
    if (aiRunInFlightRef.current) {
      setPlacementMsg("The previous removal is still finishing in the background — try again in a moment.");
      logger?.log("warn", "Editor · Auto-clean refused — a cancelled run is still executing (no concurrent inference)");
      return;
    }
    logger?.log("info", "Editor · Auto-clean (AI background removal) requested");
    const src = readSource();
    const canvas = editorRefs.sourceRef.current;
    const ctx = canvas ? get2dContext(canvas) : null;
    if (!src || !canvas || !ctx || typeof ctx.putImageData !== "function") return;
    // Snapshot the matte quality ONCE so a mid-run Fast⇄Quality toggle can't mismatch the
    // model chosen, the timeout used, and the messages shown within a single run.
    const quality = matteQuality;
    // Claim this run. If a newer run starts or the source image swaps, this id goes stale and
    // the commit at the end is skipped (never paint an old matte onto a new image).
    const reqId = ++autoRemoveReqRef.current;
    // ED-9: `stale()` is the single "this run was cancelled / superseded" test. EVERY UI write
    // below goes through it, because Cancel used to leave the (still-running) pass free to stomp
    // the status line, write its print area onto the SWAPPED image, and clear a NEWER run's busy
    // flag. `aiRunInFlightRef` additionally blocks a concurrent second inference until this one
    // actually finishes (see the guard at the top).
    const stale = () => autoRemoveReqRef.current !== reqId;
    /**
     * Give `aiBusy` back on a stale return.
     *
     * `stale()` swallows every other UI write of a superseded run, and it used to swallow this
     * one too, on the reasoning that an old pass must not release a NEWER run's "Working…". But
     * staleness has three sources and only one is a newer run: `cancelAutoRemoveBg` bumps the id
     * and releases the flag itself, while `sourceResetRef` (a new `caseImageUrl` — which is what
     * the bake's hosted `generated_url` produces once it uploads) and `resetForView` bump the id
     * and release nothing. The pass then returned into an owner-less flag and it latched TRUE for
     * the life of the editor, disabling BOTH removal controls (`aiBusy` is a term in each).
     *
     * Releasing it here is unconditional because there is never a newer run to steal it from:
     * `aiRunInFlightRef` refuses a second pass until this one returns, and this ref is cleared in
     * the same synchronous step as every call below — so no other run can have claimed `aiBusy`
     * in between. "Still refuses a concurrent second run while the cancelled pass is executing"
     * (`MaskEditor.test.tsx`) is what pins that; if it ever goes, this needs run ownership.
     */
    const releaseBusy = () => setAiBusy(false);
    aiRunInFlightRef.current = true;
    setAiBusy(true);
    // The AI matte needs the on-device model, cached once in IndexedDB. On a FRESH install it isn't
    // there yet. We USED to dead-end here and tell the user to click the sidebar "Update" button —
    // but nobody connected "Update" (which reads as an app-updater) to "download the matte model",
    // so "Remove background (AI)" looked completely broken. Instead, SELF-SERVE it: probe the cache
    // and, when the model is missing, download it inline now with visible progress (reusing the same
    // streaming machinery the Update button uses — see getSegmentationModelForQuality's onProgress
    // below), then continue the removal automatically. The sidebar Update button still works; this
    // just stops the editor from refusing on first use.
    let needsDownload = false;
    try {
      needsDownload = !!engine.removeBackgroundAI && !(await isModelCachedById(modelIdForQuality(quality)));
    } catch { /* cache probe is best-effort → assume cached; the streamed fetch below self-heals */ }
    if (stale()) { aiRunInFlightRef.current = false; releaseBusy(); return; }
    setPlacementMsg(needsDownload ? "Downloading AI model…" : "Removing background (AI)…");

    // APPROVED FLOW: the print area comes from the synthetic MAGENTA marker baked into the
    // generated image. Key it on the ORIGINAL bytes (the marker is intact there) and adopt
    // the clean quad — no colour picking. Best-effort; the transparent-region detector below
    // is the fallback when no marker is present (legacy/opaque templates).
    markerRef.current = null;
    /** True once THIS run has SETTLED the print area — written a marker-derived quad, or
     *  deliberately kept the operator's — so the fallback transparent-region detector in
     *  `applyMatteToSource` does not overwrite that decision. */
    let printAreaSet = false;
    /** True when this run left corners the operator drew alone, so the removal can say so. */
    let keptPrintAreaByHand = false;
    try {
      const orig = originalBytesRef.current;
      if (orig && engine.detectMagentaMarker) {
        const m = shadedMagentaGuide && !markerColor && engine.detectShadedMagentaMarker
          ? await engine.detectShadedMagentaMarker(orig)
          : await engine.detectMagentaMarker(orig, 0, markerColor, markerTol);
        markerRef.current = m;
        if (m.coverage >= 0.01) {
          // Fit the print quad to the largest KEYED (transparent) region with connected-
          // component noise rejection, instead of the marker's fragile diagonal-extreme
          // quad where one stray keyed pixel dragged a corner to a random image edge (the
          // "random shape" bug). Fall back to the marker quad if the region is degenerate.
          // WS-I: the SAME precedence the bake uses, through the same helper, so the auto-detected
          // print quad and the baked print mask can never come from different planes.
          const probe = await printAlphaFor(src, m);
          const printAlpha = probe?.alpha ?? null;
          if (probe) logger?.log("info", `Editor · print mask from ${probe.from}`);
          const alphaQuad = printAlpha ? printQuadFromAlpha(printAlpha, src.width, src.height) : null;
          const q = alphaQuad ?? m.quad;
          // A cancelled run must NOT write its print area — by now the operator may have swapped
          // the case image, and this quad belongs to the OLD one.
          // Both keying paths follow the same rule: detection never replaces corners the
          // operator placed. `printAreaSet` is true either way — the fallback detector below
          // must not overwrite what this branch decided to keep.
          if (q && !stale()) {
            if (printAreaIsByHand()) keptPrintAreaByHand = true;
            else writeDetectedPrintArea(q);
            printAreaSet = true;
          }
          logger?.log(
            "info",
            `Editor · print area from ${alphaQuad ? "keyed transparent region" : "marker quad (fallback)"}`,
          );
        }
      }
    } catch { /* marker detection is best-effort */ }

    // AI-ONLY (no classical fallback, spec 022): the on-device neural segmenter must produce
    // the mask. It's applied as ALPHA — non-destructive, so the object is fully protected and
    // ONLY the background is removed (clean edges, mask reusable to add a background at batch
    // render). If the AI engine isn't available or returns nothing, surface a clear error and
    // LOG it (full detail) — never silently fall back to the rough classical remover.
    // Platform-global super-admin calibration (spec 022) — read ONCE; it drives the native
    // saliency post-processing (gain/gamma/black-point) AND the edge refine below.
    const calib = resolveCalibration();
    let alpha: Uint8Array | null = null;
    try {
      if (!engine.removeBackgroundAI || typeof canvas.toDataURL !== "function") {
        throw new Error("On-device AI segmenter isn't available in this build.");
      }
      // Fetch the model bytes (memory → IndexedDB → streamed once from R2). When it isn't cached
      // yet this performs the one-time download; surface its %/speed/ETA through the SAME status
      // line so first-run is a visible progress readout, not a silent freeze. formatSpeed/formatEta
      // match the sidebar Update button's wording. A cached model never invokes onProgress, so the
      // "Removing background (AI)…" message set above simply stays.
      const model = await getSegmentationModelForQuality(quality, (p) => {
        if (stale()) return; // a cancelled download must not keep driving the status line
        setPlacementMsg(
          p.pct > 0
            ? `Downloading AI model… ${Math.round(p.pct * 100)}% (${formatSpeed(p.bytesPerSec)}${p.etaSec ? `, ${formatEta(p.etaSec)} left` : ""})`
            : `Downloading AI model… ${(p.loaded / (1024 * 1024)).toFixed(1)} MB`,
        );
      });
      if (!model) {
        // A genuine download failure (offline / R2 unreachable / stalled) vs. an already-cached
        // model that somehow failed to load — keep the message honest either way.
        throw new Error(
          needsDownload
            ? "AI model download failed — check your connection and try again."
            : "AI segmentation model isn't installed/loaded.",
        );
      }
      // Download done → return the status to the removal phase before the (possibly ~12s) inference.
      if (needsDownload && !stale()) setPlacementMsg("Removing background (AI)…");
      // Cache the model NATIVE-side once (only when it changes) so removeBackgroundAI doesn't
      // re-serialize the ~109 MB model bytes across the boundary on every call — that
      // Array.from-on-a-109MB-buffer is what froze the editor + the progress bar each removal.
      if (engine.loadSegmentationModel && loadedSegmentationModel() !== model) {
        // Timeout: the native model load (ONNX parse + EP init — tens of seconds for the
        // 489 MB full model, minutes on a struggling DirectML) had NO guard, so a wedged
        // load left the button on "Working…" forever with no recourse (the Windows "gets
        // stuck, nothing works" report). Same recover-always shape as the inference below.
        await Promise.race([
          engine.loadSegmentationModel(model),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("AI model load timed out — try again, or switch to Fast matte.")), 120_000),
          ),
        ]);
        setLoadedSegmentationModel(model);
      }
      const png = dataUrlToBytes(canvas.toDataURL("image/png"));
      // Timeout so the UI ALWAYS recovers instead of appearing dead if a machine can't finish
      // (the raw-body IPC fix removes the memory blow-up; this guards a slow/overloaded device).
      const timeoutMs = quality === "fast" ? 30_000 : 90_000;
      const maskPng = await Promise.race([
        engine.removeBackgroundAI(png, nativeMaskParams(calib)),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("AI-MATTE-TIMEOUT")), timeoutMs),
        ),
      ]);
      alpha = maskPng ? await decodeMaskAlpha(maskPng, src.width, src.height) : null;
      if (!alpha) throw new Error("AI segmenter returned no mask.");
    } catch (e) {
      aiRunInFlightRef.current = false;
      // A cancelled run's failure is not the user's problem any more — swallow the UI writes.
      // `releaseBusy` is the one exception: the flag is this pass's to give back (see it).
      if (stale()) { releaseBusy(); return; }
      const timedOut = e instanceof Error && e.message === "AI-MATTE-TIMEOUT";
      setPlacementMsg(
        timedOut
          ? "Background removal is taking too long on this machine — switch to Fast matte, use a smaller image, or close other apps, then try again."
          : `Auto-clean failed — ${e instanceof Error ? e.message : String(e)} (AI required; no classical fallback).`,
      );
      logger?.log(
        "error",
        `Editor auto-clean (AI background removal) failed — ${e instanceof Error ? (e.stack ?? e.message) : String(e)}`,
      );
      setAiBusy(false);
      return;
    }
    // The native pass is done — release the "no concurrent inference" latch so the operator can
    // start a fresh removal even if they cancelled this one.
    aiRunInFlightRef.current = false;
    // Bail if this run was superseded (Cancel, a newer removal, or a source swap bumped the id) —
    // the matte was computed against a source that's no longer displayed. The busy flag is still
    // this pass's to give back (see `releaseBusy`).
    if (stale()) { releaseBusy(); return; }
    // Key the source by the matte, protect the print area, despill/refine, paint it back, and
    // flip on `removed`. The reopen restore replays a SAVED matte through the same helper.
    if (applyMatteToSource(alpha, {
      detectPrintArea: !printAreaSet,
      message: keptPrintAreaByHand ? `Background removed (on-device AI). ${KEPT_BY_HAND}` : undefined,
    })) {
      logger?.log(
        "info",
        "Editor · Background removed (on-device AI) — object protected, product mask created",
      );
      // Removal alone must PERSIST the matte: previously only the "Preview mockup" button (or a
      // texture/test-design pick) baked, so an admin who removed the background and hit Save left
      // the matte in device memory only → reopening asked them to re-do the AI removal, and other
      // devices rendered design-only. Trigger a full bake+upload (uploadBake=true) here so the
      // matte/shell/mask + cloud refs flow through onBaked. Runs when a versionId is set (editor)
      // OR onBaked is present (wizard EMIT-ONLY mode — so a wizard removal-alone still emits its
      // payload for the post-submit upload) and !locked (previewMockup self-guards on locked too).
      // This upload-bake compose is what PERSISTS the removal (matte/shell/mask + refs); it is not
      // about display. The debounced live-preview effect MAY still compose once more ~320ms later —
      // on a marker template `markerRef` is set during this removal, so its gate passes even with no
      // test design / picked colour — but that pass runs uploadBake=false, so it never double-uploads
      // (same pixels, no R2 write). On a plain opaque template with nothing composable it stays a
      // no-op. Either way this call is the one that syncs.
      if ((versionId || onBaked) && !locked) void previewMockupRef.current(true, "persist");
    }
    setAiBusy(false);
  };

  /**
   * Apply a RAW AI matte (255 = keep) to the source canvas → the "background removed" state:
   * key the source alpha by the matte, protect the print area, despill/refine per calibration,
   * paint it back, and flip on `removed` (+ cache the matte for AI-edge calibration). Shared by
   * the live on-device removal AND the reopen RESTORE, which replays a saved matte so the
   * calibration slider works without re-running the ~12s segmenter. Returns the keyed pixels,
   * or null if the source canvas is unreadable.
   */
  /** The signature of the matte-side calibration sliders that reshape the baked cutout. Compared
   *  against `appliedMatteParamsRef` (what's currently keyed into the source canvas) to decide
   *  whether a re-key is needed before a compose. Single source of truth for the three call sites
   *  (applyMatteToSource records it, the live effect gates on it, bakeNow reads it via the ref). */
  type MatteCalibration = { edgeChoke: number; edgeFeather: number; matteStrength: number };
  // `override` lets the restore path stamp the signature of the SAVED calibration it just keyed with
  // (F1) — before the setEdge*/setMatteStrength state has committed, so the closure state still holds
  // the mount seed. Passing the same override to applyMatteToSource keeps the stamped sig in lockstep
  // with the pixels it actually produced. Absent → the live slider state (the normal path).
  // The per-stage toggles always read the LIVE closure (there's no restore-path F1
  // race for them — see the persistence restore effect), so they're appended to both branches
  // unconditionally; only the three numeric calibration params vary with `override`.
  // A CHROMA key has no calibration at all — none of the six inputs reach the pixels — so its
  // canvas signature is this constant. Every sig-guard (live mockup effect, matte-only effect,
  // bakeNow) then compares "chroma" with "chroma" and no re-key is ever scheduled.
  // The key's options join the signature, so a slider move re-keys and a bake never keeps
  // pixels keyed with older options.
  const CHROMA_SIG = `${shadedMagentaGuide ? "chroma-shaded" : "chroma"}|${JSON.stringify(guideOptions)}`;
  const matteParamsSig = (override?: MatteCalibration) =>
    matteKindRef.current === "chroma" ? CHROMA_SIG :
    `${override ? override.edgeChoke : edgeChoke}|${override ? override.edgeFeather : edgeFeather}|` +
    `${override ? override.matteStrength : matteStrength}|${stageDespill}|${stageRefine}|${stageMatteStrength}`;
  editorRefs.matteParamsSigRef.current = matteParamsSig();

  const applyMatteToSource = (
    alpha: Uint8Array | Uint8ClampedArray,
    opts?: {
      detectPrintArea?: boolean; message?: string | null; calibration?: MatteCalibration;
      /** CHROMA branch: the keyer's finished RGBA (`keyImage().out`). Present → the canvas gets
       *  these bytes verbatim and every AI-matte stage (strength remap, protect product, despill,
       *  edge refine) is skipped — the key already despilled the panel and its alpha IS the cut. */
      chroma?: { out: Uint8ClampedArray };
    },
  ): Uint8ClampedArray | null => {
    const src = readSource();
    const canvas = editorRefs.sourceRef.current;
    const ctx = canvas ? get2dContext(canvas) : null;
    if (!src || !canvas || !ctx || typeof ctx.putImageData !== "function") return null;
    const calib = resolveCalibration();
    // F1: the reopen-restore path passes the SAVED choke/feather/strength explicitly because it keys
    // in the SAME synchronous pass it sets those states — so the closure still holds the mount seed.
    // Every read of the three params below (remap, refine, and the stamped signature) goes through
    // this override so the pixels AND the recorded sig reflect the saved calibration. Absent → the
    // live slider state (the normal removal + slider-drag path), byte-identical to before.
    const cal = opts?.calibration;
    const effChoke = cal ? cal.edgeChoke : edgeChoke;
    const effFeather = cal ? cal.edgeFeather : edgeFeather;
    const effStrength = cal ? cal.matteStrength : matteStrength;
    const chroma = opts?.chroma;
    editorRefs.matteAlphaRef.current = alpha; // for the opt-in "AI edge" print-mask (content-aware keying)
    matteKindRef.current = chroma ? "chroma" : "ai";
    matteClearedRef.current = false; // a matte is (re)applied → any prior deliberate-clear is void
    // The skip badge derives from `aiStatus.aiEdge === "skipped"`; a fresh matte's verdict is
    // re-derived by the compose that always follows a removal/re-key, so nothing to clear here.
    // Key from the PRISTINE decoded pixels (Task 7) so re-applying the matte with different
    // params always recomputes from the original image instead of compounding erosion on the
    // already-keyed canvas — the invariant the calibration sliders (next task) rely on. Fall back
    // to the live canvas read when the pristine snapshot is unavailable (older edge paths / tests).
    // CRITICAL: on the FIRST application the canvas still equals pristine, so the output is
    // byte-identical to the pre-refactor behaviour either way.
    const pristine = pristineRgbaRef.current;
    const base = pristine && pristine.data.length === src.data.length ? pristine.data : src.data;
    // F6: copy into a reused RGBA scratch (reallocated only on a size change) instead of
    // `new Uint8ClampedArray(base)` every re-key. `.set` is a straight copy, so `keyed` is
    // byte-identical to the old fresh allocation; it never aliases `base` (pristine/live
    // snapshot) or `chroma.out` since it's a distinct buffer. If despill/refineMatte run they
    // reassign `keyed` to their own fresh planes (the scratch is then just dropped).
    if (!scratchRgbaRef.current || scratchRgbaRef.current.length !== base.length) {
      scratchRgbaRef.current = new Uint8ClampedArray(base.length);
    }
    let keyed: Uint8ClampedArray = scratchRgbaRef.current;
    if (chroma) {
      // CHROMA: the keyer's output IS the finished cutout — its alpha is the cut and its RGB is
      // already despilled/unpremultiplied. Every stage below would corrupt it: protect product
      // restores the panel's opaque alpha (un-keying it entirely), and choke/feather erode an
      // edge the key resolved analytically. Byte-for-byte with the Transparent tab, by copy.
      keyed.set(chroma.out);
    } else {
      // MATTE STRENGTH (B3): raise the matte's black point to strip low-confidence halo/fill from
      // the cutout BEFORE keying. `remapMatteStrength` is pure — strength 0 returns the SAME `alpha`
      // reference (identity, no allocation); strength > 0 allocates a fresh plane. Either way it
      // never mutates `alpha`, so the raw `matteAlphaRef.current` (kept for AI edge + persistence)
      // stays intact and repeated applies at the same strength are idempotent.
      // F6: reuse a matte-plane scratch for the strength remap (sized to the matte; only
      // touched when strength>0 — identity at 0 still returns the SAME `alpha` ref). Never
      // aliases `alpha`, so the raw matteAlphaRef stays intact.
      if (!scratchMatteRef.current || scratchMatteRef.current.length !== alpha.length) {
        scratchMatteRef.current = new Uint8Array(alpha.length);
      }
      // Stage toggle: "Matte strength" OFF is a no-op (identity remap), same as strength 0.
      const eff = remapMatteStrength(alpha as Uint8Array, stageMatteStrength ? effStrength : 0, scratchMatteRef.current);
      keyed.set(base);
      for (let i = 0; i < src.width * src.height; i++) {
        keyed[i * 4 + 3] = Math.min(base[i * 4 + 3], eff[i]);
      }
      // Protect product (US3): restore the original alpha inside the print-area polygon so
      // the remover can't eat the product/design region (the "it removes the case" fix).
      if (protectProduct && printArea.length >= 3) {
        // WS-J / F-38 (AUD-2 IM-6's worst offender): this used to call `pointInPolyNorm` PER PIXEL
        // — 4.19 M px × 4 edges ≈ 16.8 M non-inlined operations, 58 ms at 2048². `forEachPixelInPolyNorm`
        // evaluates the SAME predicate once per row; `scanline.test.ts` compares the whole visited
        // set against `pointInPolyNorm` on five shapes at two sizes, symmetric difference zero.
        // WS-R / C-3: `protectRampPx` defaults to 0, which IS the walk above, byte for byte.
        protectProductAlpha(keyed, base, src.width, src.height, printArea, protectRampPx);
      }
      // EDGE REFINE the AI mask per the platform calibration (spec 022) + the operator's per-template
      // sliders: neutralize residual green spill (despill_amount, self-gates to green pixels so it's a
      // no-op on neutral images), then choke (+) / spread (−) and feather the matte so the cut is
      // clean. `edgeChoke` / `edgeFeather` default to the admin's edge_choke_px / edge_feather_px;
      // with the current 1px default feather the edge is softened out of the box (the "grainy edge"
      // fix), and setting feather 0 restores the legacy crisp cut. The sliders tune both live.
      if (stageDespill && calib.despill_amount > 0) {
        keyed = despillGreen(keyed, src.width, src.height, {
          amount: calib.despill_amount,
          preserveLuma: calib.preserve_luma,
        });
      }
      if (stageRefine && (effChoke !== 0 || effFeather > 0)) {
        keyed = refineMatte(keyed, src.width, src.height, {
          radius: effChoke,
          feather: effFeather,
        });
      }
    }
    try {
      const img = ctx.createImageData(src.width, src.height);
      img.data.set(keyed);
      ctx.putImageData(img, 0, 0);
      setRemoved(true);
      setRemovalVer((v) => v + 1);
      // Fit the print area to the region this pass just keyed transparent. The AI path has
      // usually written a marker-derived quad already and passes `detectPrintArea: false` to say
      // so; anything that still asks for detection gets it.
      //
      // This used to read `!markerRef.current?.quad` as "the caller already set the print area".
      // `markerRef` is a detection CACHE, though — every compose writes it (`useMockupBake`), as
      // does the keyed-% readout — so once anything had previewed, "Print guide key (no AI)" (which
      // sets no print area of its own) silently left the print area untouched, and on a template
      // that had none it stayed empty. Whether the caller already wrote a quad is the caller's
      // knowledge, so the caller states it.
      let keptByHand = false;
      printAreaMissedRef.current = false;
      if (opts?.detectPrintArea) {
        if (printAreaIsByHand()) {
          keptByHand = true;
        } else {
          let quad: Polygon | null | undefined;
          if (chroma && shadedMagentaGuide) {
            // Existing transparent background is not ink. Fit only the alpha
            // removed by this key, matching the coverage the bake consumes.
            const printAlpha = new Uint8Array(src.width * src.height);
            for (let i = 0; i < printAlpha.length; i++) {
              const sourceAlpha = base[i * 4 + 3];
              printAlpha[i] = sourceAlpha ? Math.round(255 * (1 - keyed[i * 4 + 3] / sourceAlpha)) : 0;
            }
            quad = printQuadFromAlpha(printAlpha, src.width, src.height);
          } else quad = chroma
            ? keyedPrintQuad(keyed, src.width, src.height)
            : detectTransparentRegion(keyed, src.width, src.height)?.quad;
          if (quad) {
            // The keyed quad is exact already; snapping its corners would skew it.
            writeDetectedPrintArea(chroma ? quad : snapToEdges(quad, { data: keyed, width: src.width, height: src.height }));
          } else {
            // WS-AUTOKEY fix round: the key ran but nothing it keyed could be fitted with a quad.
            printAreaMissedRef.current = true;
          }
        }
      }
      if (opts?.message !== null) {
        const msg = opts?.message ?? "Background removed (on-device AI).";
        setPlacementMsg(keptByHand ? `${msg} ${KEPT_BY_HAND}` : msg);
      }
      // Record which calibration-slider params are now baked into the canvas, so the live mockup
      // effect can tell a matte-side slider move (needs a re-key) from a bg/blend tweak (doesn't).
      // Uses the SAME override as the pixels (F1) so the restore's stamp matches the saved calibration
      // it just keyed with — once the setEdge* state commits, matteParamsSig() equals this, so every
      // live sig-guard (mockup effect, matte-only effect, bakeNow) then no-ops → a single restore pass.
      editorRefs.appliedMatteParamsRef.current = matteParamsSig(cal);
      return keyed;
    } catch {
      /* unreadable canvas — leave source untouched */
      return null;
    }
  };

  /**
   * Idempotent REPLAY of the cached matte (Task 7). Re-applies `matteAlphaRef` to the source from
   * the PRISTINE snapshot with the CURRENT calibration/params — because `applyMatteToSource` now
   * keys from the pristine decoded image, calling this repeatedly recomputes the removed image
   * from scratch each time instead of compounding erosion. This is the foundation the next task's
   * calibration sliders (edge feather / choke / matte strength) re-apply on. No-op → returns false
   * when locked, before any removal, or with no cached matte. `message: null` keeps the status line
   * untouched; the `removalVer` bump inside re-runs the live-compose effect, which handles bake
   * dirtiness (no duplicate marking here). Component-internal; also surfaced on the bake handle. */
  /**
   * Run the magenta chroma key on the PRISTINE pixels and put its RGBA on the canvas verbatim.
   * This — not a replay of the cached alpha plane — is how every chroma re-key happens (first
   * click, slider/stage re-key, undo→redo, reopen restore): the keyer is deterministic and pure,
   * so re-running it is both cheaper than storing the RGBA and guaranteed byte-identical to the
   * Transparent tab. Returns false when the canvas is unreadable or the image has no magenta.
   */
  const rekeyChroma = (opts?: { detectPrintArea?: boolean; message?: string | null }): boolean => {
    const src = readSource();
    if (!src) return false;
    const pristine = pristineRgbaRef.current;
    const base = pristine && pristine.data.length === src.data.length ? pristine.data : src.data;
    const res = chromaKeyFromPristine(base, src.width, src.height, shadedMagentaGuide, guideOptions);
    if (!res) return false;
    if (shadedMagentaGuide) {
      let keyed = 0;
      for (let i = 0; i < res.alpha.length; i++) {
        const sourceAlpha = base[i * 4 + 3];
        if (sourceAlpha && res.alpha[i] / sourceAlpha < 0.5) keyed++;
      }
      keyCoverageRef.current = keyed / Math.max(1, res.alpha.length);
    } else keyCoverageRef.current = inkFraction(res.alpha, 128);
    // The alpha plane is still cached (matteAlphaRef) — AI edge and the persisted matte PNG read it.
    return applyMatteToSource(res.alpha, {
      chroma: { out: res.out },
      detectPrintArea: opts?.detectPrintArea ?? false,
      message: opts?.message ?? null,
    }) !== null;
  };

  /**
   * The print-area coverage plane for THIS image, from the one AUTO keyer (WS-I). Returns the
   * source alongside it so every call site logs which keyer answered — the audit's complaint was
   * never "the wrong mask", it was "no way to tell which mask you got".
   * Precedence: a PICKED colour is an explicit instruction and wins; otherwise the chroma key;
   * otherwise (no magenta) the engine marker, so a template that keyed before still keys.
   */
  const printAlphaFor = async (
    src: { data: Uint8ClampedArray; width: number; height: number },
    marker: MagentaMarkerResult | null,
  ): Promise<{ alpha: Uint8Array; from: string } | null> => {
    if (!markerColor) {
      const base = pristineRgbaRef.current;
      const pixels = base && base.data.length === src.data.length ? base.data : src.data;
      const chroma = chromaMarkerFromPristine(pixels, src.width, src.height, shadedMagentaGuide, guideOptions);
      if (chroma) return { alpha: chroma.printAlpha, from: "chroma key (same as the preview matte)" };
    }
    if (!marker) return null;
    const decoded = await decodeMaskAlpha(marker.printMaskPng, src.width, src.height);
    return decoded
      ? { alpha: decoded, from: markerColor ? "engine marker (picked colour)" : "engine marker (no magenta for the chroma key)" }
      : null;
  };

  const reapplyMatte = (): boolean => {
    if (locked || !removed || !editorRefs.matteAlphaRef.current) return false;
    // A chroma cutout is re-KEYED, never replayed: its alpha alone would go back through the
    // AI-matte stages and lose the despilled colours the key produced.
    if (matteKindRef.current === "chroma") return rekeyChroma();
    return applyMatteToSource(editorRefs.matteAlphaRef.current, { detectPrintArea: false, message: null }) !== null;
  };
  reapplyMatteRef.current = reapplyMatte;
  // ED-8: let the history helpers (declared above `applyMatteToSource`) replay a stashed matte.
  // `undoneMatteKindRef` covers the redo-a-removal direction, where `resetBackground` has already
  // cleared the live kind alongside the matte it stashed.
  applyMatteToSourceRef.current = (alpha) =>
    (matteKindRef.current ?? undoneMatteKindRef.current) === "chroma"
      ? rekeyChroma({ message: "Background removal re-applied." })
      : applyMatteToSource(alpha, {
          detectPrintArea: false,
          message: "Background removal re-applied.",
        }) !== null;

  // B5 — LIVE calibration on the pure background-cutout case: when there's NO composable mockup
  // (no test design, no picked/detected marker) the mockup effect above bails, so dragging a
  // calibration slider would show nothing. This debounced effect re-keys the source from pristine
  // with the fresh params; `reapplyMatte` bumps `removalVer`, which the recomposite effect uses to
  // repaint `displayUrl`. Its gate is the COMPLEMENT of the mockup effect's, so the two are mutually
  // exclusive — a slider move drives exactly one of them (never a double compose).
  // A print guide key re-keys when its options move, whatever else is on screen.
  const guideSig = JSON.stringify(guideOptions);
  useEffect(() => {
    if (locked || !removed || matteKindRef.current !== "chroma") return;
    const id = setTimeout(() => {
      if (editorRefs.appliedMatteParamsRef.current !== editorRefs.matteParamsSigRef.current) reapplyMatteRef.current();
    }, 320);
    return () => clearTimeout(id);
  }, [guideSig, locked, removed, editorRefs, reapplyMatteRef]);

  useEffect(() => {
    if (locked || !removed || !editorRefs.matteAlphaRef.current) return;
    if (testDesignRef.current || markerColor || markerRef.current) return;
    const id = setTimeout(() => {
      // Only re-key when the live params differ from what's already keyed. A real slider drag moves
      // the sig → re-key; the reopen restore already keyed with the saved params in one pass and
      // stamped the matching sig (F1), so this no-ops instead of a wasteful full-res double-key.
      // J-13: through the two refs the file already maintains for the live sig / the live
      // `reapplyMatte` closure. Listing those closures as deps instead would restart this 320 ms
      // debounce on EVERY render — and making them `useCallback`s would cascade the change into
      // `applyMatteToSource`, the one function in this wave that three field bugs are written into.
      // The refs are stable, so the list below is honest, and they are strictly more current than
      // the closures captured when the effect ran.
      if (editorRefs.appliedMatteParamsRef.current !== editorRefs.matteParamsSigRef.current) reapplyMatteRef.current();
    }, 320);
    return () => clearTimeout(id);
    // Stage toggles (despill/refine/strength) reshape the matte the same way a calibration-slider
    // drag does, so they must also drive this re-key.
    // J-13: `locked`, `removed` and `markerColor` join the list — the body already GATES on all
    // three, and omitting them was the lie. Behaviour change, accepted by the plan: picking a
    // marker colour now CANCELS a pending re-key instead of letting a stale one land 320 ms later.
  }, [
    edgeFeather, edgeChoke, matteStrength, stageDespill, stageRefine, stageMatteStrength,
    locked, removed, markerColor, editorRefs, testDesignRef, markerRef, reapplyMatteRef,
  ]);

  // Restore the "background removed" STATE from a previously-baked version: fetch the saved
  // raw matte, replay it through applyMatteToSource so the source shows removed + the AI-remove
  // button reads "Restore background", and re-arm the AI-edge toggle + calibration slider (which
  // reads the raw matte). Without this, reopening a saved template reverted every AI edit to its
  // un-run state and calibration was impossible (the slider needs a matte). Waits for the source
  // canvas (chromaReady) since the matte is keyed onto its pixels. Runs once per case image.
  // Flips true ONLY after a SUCCESSFUL restore (the one-shot-per-image guard). Previously it was set
  // BEFORE the async fetch, so a single transient fetch/decode failure was PERMANENT — the effect
  // never retried and the next Save then WIPED the matte via the cloud full-overwrite persist. Now a
  // failure leaves it false so the banner's Retry (or a later effect run) re-attempts.
  const restoredMatteRef = useRef(false);
  // Re-entrancy guard: an effect re-run OR a Retry click must not launch a SECOND concurrent restore
  // while one is in flight (double-keying / racing setState).
  const matteRestoreInFlightRef = useRef(false);
  /**
   * WS-ERR F-6 residual (b) — which VIEW the in-flight restore belongs to.
   *
   * `resetForView` bumps it. A run whose generation is stale must neither publish its result
   * (the previous view's matte over the new view's canvas) nor lower the new view's flags — and
   * crucially it must not hold the in-flight lock, or switching tabs mid-download left the new
   * tab reading "not removed" for ever with no retry banner, because nothing had failed.
   */
  const restoreGenRef = useRef(0);
  // FAILURE LATCH: set in the catch, checked by the effect guard. TemplateEditor rebuilds
  // `initialLayerRefs` with a fresh identity every render, so the restore effect (dep on it) re-runs
  // on EVERY re-render. Without this latch a persistent/unfetchable matte would re-fetch + re-log a
  // structured ERROR on every keystroke/unrelated re-render. Latching means a failed restore is
  // retried ONLY when the user clicks the banner's Retry (which clears the latch first) — one error
  // log per real attempt, no per-render flood. Reset alongside `restoredMatteRef` on case-image change.
  const matteRestoreFailedRef = useRef(false);
  // Surfaces the retry banner after a restore failure; cleared on a successful restore.
  const [matteRestoreFailed, setMatteRestoreFailed] = useState(false);
  /**
   * The restore is IN FLIGHT. Without this the editor mounted silently as "not removed" for
   * as long as the matte fetch took — a wrong state with nothing on screen saying so, and a
   * Save made during it would have persisted the wrong state. The rail summary and the
   * removal card both read it, and the fetch now carries a 20 s deadline
   * (`MATTE_RESTORE_TIMEOUT_MS`) rather than the general 60 s download budget.
   */
  const [matteRestoring, setMatteRestoring] = useState(false);

  // Restore the "background removed" STATE from a previously-baked version: fetch the saved raw
  // matte, replay it through applyMatteToSource so the source shows removed + the AI-remove button
  // reads "Restore background", and re-arm the AI-edge toggle + calibration slider (which reads the
  // raw matte). Shared by the mount effect AND the retry banner so both replay the EXACT same pass.
  const runMatteRestore = async () => {
    const refs = initialLayerRefs;
    // `matteRestoreFailedRef` latches a prior failure so the per-render effect (fresh `initialLayerRefs`
    // identity each render) can't auto-retry/re-log — only the banner's Retry (which clears the latch)
    // re-attempts. Success sets `restoredMatteRef`; in-flight blocks concurrent runs.
    // WS-ERR F-6: the runner owns `matteRestoring` on EVERY exit, including this one. The
    // banner's Retry used to raise the flag itself and then call this — and any of the five
    // clauses below returns before the `try`, so the `finally` never ran and "Restoring the
    // saved background removal…" stayed on screen for the rest of the session over an editor
    // that was doing nothing. `!chromaReady` is the clause an impatient operator actually hits:
    // the case image has not decoded yet, which is exactly when they press Retry.
    if (!refs?.matteUrl || restoredMatteRef.current || matteRestoreFailedRef.current || matteRestoreInFlightRef.current || !chromaReady) {
      // F-6 residual (a): only the run that OWNS the flag may lower it. A re-entrant call that
      // bounced off `matteRestoreInFlightRef` used to clear it here — so a Retry press, or an
      // effect re-run, said "nothing is happening" over a download that was still going.
      if (!matteRestoreInFlightRef.current) setMatteRestoring(false);
      return;
    }
    const gen = restoreGenRef.current;
    /** This run is still the one the editor is waiting for. */
    const current = () => gen === restoreGenRef.current;
    matteRestoreInFlightRef.current = true;
    setMatteRestoring(true);
    try {
      // ── Saved state that is INDEPENDENT of the cutout technique ──────────────────────────────
      // AI edge (+ its keep radius) and the edge calibration are restored for BOTH kinds; only the
      // PIXEL replay below differs. This used to sit under the chroma early-return, so reopening a
      // chroma template with AI edge saved ON came back with the toggle OFF — and because the bake
      // emits the LIVE flags, the next Save overwrote the persisted `ai_edge: true` with `false`
      // (cloud persist is a full overwrite) and the camera cut-out was gone for good. AI edge is
      // usable on a chroma cutout: the print-mask refinement reads `matteAlphaRef`, which the
      // chroma branch fills with the key's alpha plane, and the chroma bake persists both flags.
      //
      // Mark this state batch as restore-driven so the appearance-deps dirty effect (which fires on
      // the next commit) skips marking the untouched, already-persisted template dirty (F1). Bumped
      // BEFORE the setState calls so it's in place when that effect reads it. All the sets below land
      // in one commit → one dirty-effect run → one consume. CRITICAL: only bump when a value actually
      // CHANGES a dirty-effect dep — otherwise React bails the no-op sets, the dirty effect never
      // runs, the epoch is never consumed, and the user's NEXT real edit would be wrongly swallowed.
      const restoreChangesDirtyDep =
        (typeof refs.aiEdgeKeep === "number" && refs.aiEdgeKeep !== aiEdgeKeep) ||
        (typeof refs.aiEdge === "boolean" && refs.aiEdge !== aiEdge) ||
        (typeof refs.edgeFeather === "number" && refs.edgeFeather !== edgeFeather) ||
        (typeof refs.edgeChoke === "number" && refs.edgeChoke !== edgeChoke) ||
        (typeof refs.matteStrength === "number" && refs.matteStrength !== matteStrength);
      if (restoreChangesDirtyDep) editorRefs.restoreEpochRef.current += 1;
      // Restore the AI-edge toggle + slider so the print-mask refinement and the calibration UI
      // reflect the saved values. On the AI path below, `applyMatteToSource` additionally keys with
      // the same numbers SYNCHRONOUSLY via `cal` (these setStates haven't committed yet — F1).
      // On the chroma path the three calibration numbers reach no pixel at all; restoring them
      // (the bake wrote 0/0/0) is what makes the bake's stated rationale true — a later AI removal
      // in this session starts from the saved zeros instead of the mount seed.
      if (typeof refs.aiEdgeKeep === "number") setAiEdgeKeep(refs.aiEdgeKeep);
      if (typeof refs.aiEdge === "boolean") setAiEdge(refs.aiEdge);
      if (typeof refs.edgeFeather === "number") setEdgeFeather(refs.edgeFeather);
      if (typeof refs.edgeChoke === "number") setEdgeChoke(refs.edgeChoke);
      if (typeof refs.matteStrength === "number") setMatteStrength(refs.matteStrength);

      // CHROMA templates restore by RE-KEYING the pristine pixels — no fetch, no decode. The
      // saved matte is only the key's ALPHA plane; replaying it through the AI-matte stages
      // would throw away the despilled colours and let protect/choke/feather eat the panel.
      // The keyer is deterministic, so the re-key reproduces the saved pixels exactly.
      if (refs.matteKind === "chroma") {
        restoreRemovalRef.current = !removed;
        if (rekeyChroma({ detectPrintArea: false, message: "Background removal restored." })) {
          restoredMatteRef.current = true;
          setMatteRestoreFailed(false);
          logger?.log("info", "Editor · Restored saved magenta-key removal by re-keying (no matte fetch)");
          return;
        }
        // No magenta left in the pristine image (or an unreadable canvas) — fall through to the
        // ordinary replay so the removal state is at least approximated from the hosted matte.
        restoreRemovalRef.current = false;
        logger?.log("warn", "Editor · Chroma re-key found no magenta panel — falling back to the saved matte", {
          step: "restoreChromaKey", matteHost: hostOfUrl(refs.matteUrl!),
        });
      }
      // F-5: ONE budget for the headers, the body AND the decode. `fetchWithTimeout` clears its
      // timer when the RESPONSE resolves, so a server that answered 200 and then stalled
      // mid-body left this await running for ever — the very hang MATTE_RESTORE_TIMEOUT_MS was
      // added to bound, through the one door the bound did not cover. The request is ABORTED on
      // expiry, so the socket is released rather than leaking for the life of the tab.
      const alpha = await fetchBytesWithTimeout(
        refs.matteUrl!, NO_HTTP_CACHE, MATTE_RESTORE_TIMEOUT_MS, "Matte download",
        { decode: (bytes) => decodeMaskAlpha(bytes, w, h) },
      );
      if (!alpha) throw new Error("matte decode failed");
      // The saved calibration to key with NOW. A SAVED value (including 0 or a negative choke)
      // must WIN over the resolveCalibration()/0 mount seed; an ABSENT value keeps the seed (the
      // current closure state). We pass this SAME object to applyMatteToSource below so the single
      // restore pass keys with the saved values (F1) — previously it read the mount-seed closure
      // state because setEdge*/setMatteStrength hadn't committed yet, which for LOCKED templates
      // left the preview keyed with the seed calibration forever.
      const cal: MatteCalibration = {
        edgeChoke: typeof refs.edgeChoke === "number" ? refs.edgeChoke : edgeChoke,
        edgeFeather: typeof refs.edgeFeather === "number" ? refs.edgeFeather : edgeFeather,
        matteStrength: typeof refs.matteStrength === "number" ? refs.matteStrength : matteStrength,
      };
      // ED-1: the removal this replays is the template's SAVED state, not an edit. Tell the commit
      // effect to RE-BASELINE the history instead of pushing an entry — otherwise Undo came up
      // armed and its first press reverted to `backgroundRemoved:false`, wiping the restored AI
      // removal (and the next Save then dropped the hosted matte for good).
      restoreRemovalRef.current = !removed;
      const keyed = applyMatteToSource(alpha, { detectPrintArea: false, message: "Background removal restored.", calibration: cal });
      // An unreadable canvas leaves `removed` untouched, so the commit effect never runs and the
      // one-shot flag would linger and swallow the user's NEXT real edit — drop it here.
      if (!keyed) restoreRemovalRef.current = false;
      // Success: mark done (won't re-run) and clear any prior failure banner.
      restoredMatteRef.current = true;
      setMatteRestoreFailed(false);
      logger?.log("info", "Editor · Restored saved AI background removal (matte + AI-edge state)");
    } catch (err) {
      // Do NOT set restoredMatteRef (success gate). LATCH the failure so the per-render effect can't
      // auto-retry/re-log — retry becomes strictly user-initiated via the banner's Retry, which clears
      // the latch first. Upgrade the old warn to a structured error + raise the retry banner.
      // F-6 residual (b): the abandoned view's failure is not this view's failure.
      if (!current()) return;
      matteRestoreFailedRef.current = true;
      logger?.log("error", "Editor · Could not restore saved AI removal — matte fetch/decode failed", {
        error: err, step: "restoreMatte", matteHost: hostOfUrl(refs.matteUrl!),
      });
      setMatteRestoreFailed(true);
    } finally {
      // F-6 residual (b): a STALE run releases nothing. `resetForView` already cleared the lock
      // for the new view and a new run may be in flight; clearing it (or the flag) here would
      // describe the wrong view.
      if (current()) {
        matteRestoreInFlightRef.current = false;
        setMatteRestoring(false);
      }
    }
  };
  /** Always the latest `runMatteRestore` closure — it reads the live calibration state, so a
   *  captured one would replay stale params. Same pattern as `reapplyMatteRef`. */
  const runMatteRestoreRef = useRef(runMatteRestore);
  runMatteRestoreRef.current = runMatteRestore;
  useEffect(() => {
    void runMatteRestoreRef.current();
    // J-13 / MN-3: the dep is the layer refs' VALUE, not their identity. `TemplateEditor` rebuilds
    // `initialLayerRefs` with a fresh object on every render, so this effect used to re-run on
    // every keystroke anywhere in the parent — which is the entire reason the failure LATCH had to
    // exist. The latch STAYS (it is still what makes Retry user-initiated across a real key change
    // or a `chromaReady` flip), but it is no longer load-bearing for the per-render flood.
  }, [layerRefsKey, chromaReady]);

  /**
   * User-initiated retry after a failed matte restore.
   *
   * The failure LATCH is what makes retry strictly user-initiated: `TemplateEditor` rebuilds
   * `initialLayerRefs` with a fresh identity every render, so the restore effect re-runs on every
   * re-render, and without the latch a persistent/unfetchable matte would re-fetch and re-log a
   * structured ERROR on every keystroke. Clearing it here — and only here — is the whole design.
   */
  const retryMatteRestore = () => {
    matteRestoreFailedRef.current = false;
    setMatteRestoreFailed(false);
    // F-6: the flag is NOT raised here. `runMatteRestore` raises it once it is past its guard
    // and lowers it in its `finally`, so there is exactly one owner and no exit without a
    // reset. Raising it here was what stranded the line when the guard refused.
    void runMatteRestore();
  };

  /**
   * F-35 / J-14 — reset the per-view matte state on a `view` prop change instead of a remount.
   * Declared empty by J-7 so F-35 lands as one small diff against a settled seam.
   */
  const resetForView = () => {
    // Everything below is per-VIEW: it describes the image this view shows, and the next view
    // shows a different one. The four things F-35 names as survivors — the segmentation model, the
    // zoom, the loaded test design and (when `caseImageUrl` is unchanged) the decoded source — are
    // deliberately absent.
    markerRef.current = null;
    editorRefs.matteAlphaRef.current = null;
    matteKindRef.current = null;
    editorRefs.appliedMatteParamsRef.current = "";
    // A view switch is not a deliberate clear: the new view's saved matte (if any) must restore.
    matteClearedRef.current = false;
    // Re-arm the saved-matte restore for the NEW view and drop the previous view's failure state,
    // banner included — a stale "restore failed" must not follow the operator across a tab.
    restoredMatteRef.current = false;
    matteRestoreFailedRef.current = false;
    matteRestoreInFlightRef.current = false;
    setMatteRestoreFailed(false);
    // F-6 residual (b): retire the in-flight run's generation. It already lost the lock on the
    // line above, so the new view's restore can START; this is what stops the old one from
    // publishing its matte, its failure or its flags over the new view when it settles.
    restoreGenRef.current += 1;
    setMatteRestoring(false);
    // Invalidate any in-flight auto-clean so its (now stale) matte cannot paint onto the new view.
    autoRemoveReqRef.current += 1;
    setKeyedPct(null);
    setRemoved(false);
    setRemovalVer(() => 0);
  };
  /** Always the latest `resetForView` closure, so the view effect below can list only `view`. */
  const resetForViewRef = useRef(resetForView);
  resetForViewRef.current = resetForView;
  /**
   * F-35 / J-14 — run `resetForView` on a `view` CHANGE, never on the first render: the mount has
   * already produced exactly this state, and re-running it would undo the mount's own work.
   */
  const seenViewRef = useRef<string | undefined>(view);
  useEffect(() => {
    if (seenViewRef.current === view) return;
    seenViewRef.current = view;
    resetForViewRef.current();
  }, [view]);


  return {
    // refs other hooks read
    markerRef,
    matteKindRef,
    matteClearedRef,
    autoRemoveReqRef,
    reapplyMatteRef,
    restoredMatteRef,
    matteRestoreFailedRef,
    setMatteRestoreFailed,
    // actions
    matteParamsSig,
    applyMatteToSource,
    rekeyChroma,
    printAlphaFor,
    reapplyMatte,
    autoRemoveBg,
    cancelAutoRemoveBg,
    removeByMagentaKey,
    autoMagentaKey,
    runMatteRestore,
    retryMatteRestore,
    // state
    keyedPct,
    matteRestoreFailed,
    matteRestoring,
    resetForView,
  };
}
